import type { IRealChart } from '@/music/ireal/types';
import { computeLayout, type Placement } from './chartLayout';

export interface ReadingChartLayout {
  placements: (Placement & { symbolScale?: number })[];
  fontSize: number;
  rowCount: number;
}

/** Reading metrics depend on the sheet's width, never the browser breakpoint. */
export function readingFontSize(width: number, scale: number): number {
  return Math.min(48, Math.max(24, (width - 32) * 0.046)) * scale;
}

/** Leave a leading time-signature lane, including wide meters. */
export function readingGutter(chart: IRealChart, fontSize: number): number {
  const wide = chart.timeSignature.some((n) => n >= 10);
  return Math.max(wide ? 40 : 26, fontSize * (wide ? 0.92 : 0.55) + 8);
}

/** Water-fill a phrase: ordinary bars keep equal widths until a busy bar
 * needs to borrow space. Authored indents/gaps remain part of the phrase. */
function fitWidths(minimums: number[], weights: number[], width: number): number[] {
  let low = 0;
  let high = width;
  for (let i = 0; i < 40; i++) {
    const unit = (low + high) / 2;
    const total = minimums.reduce((sum, min, j) => sum + Math.max(min, weights[j] * unit), 0);
    if (total > width) high = unit;
    else low = unit;
  }
  return minimums.map((min, i) => Math.max(min, weights[i] * low));
}

/** Reflow only the authored phrase that cannot fit. No written timing,
 * repeats or imported cell metadata is mutated by the reading layout. */
export function layoutReadingChart(
  chart: IRealChart,
  width: number,
  fontSize: number,
  minimums: readonly number[],
): ReadingChartLayout {
  const authored = computeLayout(chart);
  const available = Math.max(1, width - 2 * readingGutter(chart, fontSize));
  const placements: ReadingChartLayout['placements'] = [];
  let row = 0;
  let previousAuthoredRow = 0;
  let start = 0;
  while (start < authored.length) {
    const authoredRow = authored[start].row;
    if (start === 0) row = authoredRow;
    else row += Math.max(0, authoredRow - previousAuthoredRow - 1);
    let end = start + 1;
    while (end < authored.length && authored[end].row === authoredRow) end++;
    const indices = Array.from({ length: end - start }, (_, i) => start + i);

    const draw = (group: number[], preserveSlots: boolean) => {
      const weights: number[] = [];
      const mins: number[] = [];
      const slots: { index: number | null; weight: number }[] = [];
      let cursor = 0;
      for (const index of group) {
        const place = authored[index];
        if (preserveSlots && place.col > cursor)
          slots.push({ index: null, weight: place.col - cursor });
        slots.push({ index, weight: preserveSlots ? place.span : 4 });
        cursor = place.col + place.span;
      }
      // An authored short line or second ending keeps its horizontal position.
      if (preserveSlots && cursor < 16) slots.push({ index: null, weight: 16 - cursor });
      for (const slot of slots) {
        weights.push(slot.weight);
        mins.push(
          slot.index == null
            ? 0
            : Math.min(available, Math.max(1, minimums[slot.index] ?? fontSize)),
        );
      }
      const widths = fitWidths(mins, weights, available);
      let left = 0;
      slots.forEach((slot, i) => {
        if (slot.index != null)
          placements[slot.index] = {
            row,
            col: (left / available) * 16,
            span: (widths[i] / available) * 16,
            rowStart: slot.index === group[0],
            rowEnd: slot.index === group.at(-1),
            symbolScale: Math.min(
              1,
              Math.max(1, available - fixedBarChrome(chart, slot.index)) /
                Math.max(1, (minimums[slot.index] ?? fontSize) - fixedBarChrome(chart, slot.index)),
            ),
          };
        left += widths[i];
      });
      row++;
    };

    const fit = (group: number[], preserveSlots: boolean): void => {
      const need = group.reduce((sum, index) => sum + (minimums[index] ?? fontSize), 0);
      if (need <= available || group.length === 1) {
        draw(group, preserveSlots);
        return;
      }
      // Split halves, rather than greedy wrapping that turns four-bar phrases
      // into three-plus-one. Dense authored rows keep their own grouping too.
      const middle = Math.ceil(group.length / 2);
      fit(group.slice(0, middle), false);
      fit(group.slice(middle), false);
    };
    fit(indices, true);
    previousAuthoredRow = authoredRow;
    start = end;
  }
  return { placements, fontSize, rowCount: row };
}

/** Repeat dots and borders stay crisp at small sizes; the symbol fit must
 * leave their fixed inset intact when an indivisible bar needs to shrink. */
function fixedBarChrome(chart: IRealChart, index: number): number {
  const measure = chart.measures[index];
  return 4 + (measure.open === 'repeat' ? 18 : 0) + (measure.close === 'repeat' ? 18 : 0);
}

/** The narrowest a bar may draw: its symbols side by side. Chords share the
 * bar in proportion to their beats wherever the bar has room (flex-grow), and
 * a chord narrower than its symbol keeps the symbol's width (min-width auto in
 * readingChart.css). Reserving the proportional width instead made a half-beat
 * anticipation ask for eight times its symbol, so every bar of Moment's
 * Notice's B section, which pushes a chord into the next bar, took a line. */
export function readingMeasureWidth(
  chart: IRealChart,
  index: number,
  fontSize: number,
  chordWidths: readonly number[],
): number {
  const measure = chart.measures[index];
  const padding = fontSize * 0.34 + fixedBarChrome(chart, index);
  if (measure.barRepeat || !measure.chords.length) return fontSize + padding;
  const count = measure.chords.length;
  const inner = measure.chords.reduce((sum, _, i) => sum + (chordWidths[i] ?? fontSize), 0);
  const meter = index > 0 && measure.timeSig ? fontSize * 0.9 : 0;
  return inner + padding + Math.max(0, count - 1) * fontSize * 0.14 + meter;
}
